import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box, TextField, Typography, Paper, CircularProgress, Alert, Stack
} from '@mui/material';
import * as api from './businessAreasApi';
import type { BusinessArea } from './businessAreasApi';
import { sx14, paperSx, FormRow, DetailHeader } from '../common/detailView';

interface Props {
    businessAreaId: number | null;
}

export function BusinessAreaView({ businessAreaId }: Props) {
    const { t } = useTranslation();

    const [item, setItem] = useState<BusinessArea | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [isNew, setIsNew] = useState(false);
    const [copy, setCopy] = useState<BusinessArea | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            if (businessAreaId === null) {
                setItem({ id: 0, code: '', name: '' });
                setIsNew(true);
                setIsEditing(true);
            } else {
                const data = await api.getBusinessArea(businessAreaId);
                setItem(data);
                setIsNew(false);
                setIsEditing(false);
            }
        } catch (err) {
            setError(String(err));
        } finally {
            setLoading(false);
        }
    }, [businessAreaId]);

    useEffect(() => { load(); }, [load]);

    if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
    if (error && !item) return <Alert severity="error">{error}</Alert>;
    if (!item) return null;

    const isCodeValid = !!item.code && item.code.length <= 20;

    const startEdit = () => { setCopy(JSON.parse(JSON.stringify(item))); setIsEditing(true); };
    const cancelEdit = () => { if (copy) setItem(copy); setIsEditing(false); };

    const save = async () => {
        setSaving(true); setError(null);
        try {
            const response = isNew ? await api.createBusinessArea(item) : await api.updateBusinessArea(item);
            setItem(response);
            setIsNew(false);
            setIsEditing(false);
        } catch (err) {
            setError(String(err));
        } finally {
            setSaving(false);
        }
    };

    const del = async () => {
        if (!window.confirm(t('businessarea.confirmDelete'))) return;
        setSaving(true); setError(null);
        try {
            await api.deleteBusinessArea(item.id);
            window.history.back();
        } catch (err) {
            setError(String(err));
        } finally {
            setSaving(false);
        }
    };

    const update = <K extends keyof BusinessArea>(field: K, value: BusinessArea[K]) => {
        setItem({ ...item, [field]: value });
    };

    return (
        <Paper variant="outlined" sx={paperSx}>
            <DetailHeader
                title={`${item.code ?? ''} ${item.name ?? ''}`}
                isEditing={isEditing}
                isNew={isNew}
                saving={saving}
                canSave={isCodeValid}
                onEdit={startEdit}
                onSave={save}
                onCancel={cancelEdit}
                onDelete={del}
            />

            <Box p={2}>
                <Stack spacing={2}>
                    {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

                    <FormRow label={t('businessarea.code')}>
                        {!isNew && !isEditing ? (
                            <Typography sx={sx14}>{item.code}</Typography>
                        ) : (
                            <TextField size="small" fullWidth
                                inputProps={{ maxLength: 20, style: { textTransform: 'uppercase' } }}
                                value={item.code}
                                onChange={e => update('code', e.target.value.toUpperCase())}
                                error={!isCodeValid} />
                        )}
                    </FormRow>

                    <FormRow label={t('businessarea.name')}>
                        {!isEditing ? (
                            <Typography sx={sx14}>{item.name}</Typography>
                        ) : (
                            <TextField size="small" fullWidth
                                inputProps={{ maxLength: 100 }}
                                value={item.name ?? ''}
                                onChange={e => update('name', e.target.value)} />
                        )}
                    </FormRow>
                </Stack>
            </Box>

            {saving && <Box display="flex" justifyContent="center" p={1}><CircularProgress size={20} /></Box>}
        </Paper>
    );
}
